import { apiError } from "../../../../../utils/formErrors";
import TableSkeleton from "../../../../../component/TableSkeleton";
import React, { useState, useEffect, useCallback } from "react";
import { Box, Stack, Table, TableHead, TableBody, TableRow, TableCell, Dialog, TextField, IconButton, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import TopBar from "../../../../../component/TopBar";
import MainBox from "../../../container/MainBox";
import BtnPrimary from "../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../component/SecondaryButton";
import EmptyState from "../../../../../component/EmptyState";
import { headCellSx, bodyCellSx } from "../../../../../component/listing";
import { getGradeLevels, createGradeLevel, updateGradeLevel, deleteGradeLevel } from "../../../../../services/gradeLevel";
import { useNotify } from "../../../../../component/NotificationProvider";
import { tokens, displayFont } from "../../../../../theme";

const money = (n) => (n ? `₹${Number(n).toLocaleString("en-IN")}` : "—");
const Label = ({ children }) => <Box sx={{ fontSize: "0.8125rem", fontWeight: 500, color: tokens.textMuted, mb: "0.5rem" }}>{children}</Box>;

const GradesBands = () => {
  const notify = useNotify();
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const empty = { name: "", rank: "", minCTC: "", maxCTC: "", description: "" };
  const [form, setForm] = useState(empty);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const res = await getGradeLevels();
      if (res?.success === false) throw apiError(res);
      setLevels(Array.isArray(res?.data) ? res.data : []);
    } catch (e) { setError(true); notify.error(e?.message || "Failed to load grades"); }
    finally { setLoading(false); }
  }, [notify]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = () => { setEditing(null); setForm({ ...empty, rank: levels.length ? Math.max(...levels.map((l) => l.rank || 0)) + 1 : 1 }); setDialog(true); };
  const openEdit = (l) => { setEditing(l._id); setForm({ name: l.name, rank: l.rank, minCTC: l.band?.minCTC || "", maxCTC: l.band?.maxCTC || "", description: l.description || "" }); setDialog(true); };

  const submit = async () => {
    if (!form.name.trim()) return notify.error("Name is required");
    if (form.rank === "" || form.rank == null) return notify.error("Rank is required");
    setBusy(true);
    try {
      const payload = { name: form.name.trim(), rank: Number(form.rank), description: form.description,
        band: { minCTC: Number(form.minCTC) || 0, maxCTC: Number(form.maxCTC) || 0 } };
      const res = editing ? await updateGradeLevel(editing, payload) : await createGradeLevel(payload);
      if (res?.success === false) throw apiError(res);
      notify.success(editing ? "Grade updated" : "Grade added");
      setDialog(false); fetchData();
    } catch (e) { notify.error(e?.message || "Could not save grade"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { const res = await deleteGradeLevel(id); if (res?.success === false) throw apiError(res); notify.success("Grade removed"); fetchData(); }
    catch (e) { notify.error(e?.message || "Could not remove"); }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar MainHeading={"Grades & Bands"} breadcrumbs={[{ label: "Settings", href: "/app/settings" }, { label: "Grades & Bands" }]} />
      <MainBox sx={{ padding: "1.25rem" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: "1.25rem" }}>
          <Box sx={{ fontSize: "0.875rem", color: tokens.textMuted }}>Define your leveling ladder (lower rank = more junior) and the CTC band for each level.</Box>
          <BtnPrimary onClick={openNew} sx={{ borderRadius: "8px" }}>New grade</BtnPrimary>
        </Stack>

        {loading ? (
          /* The header and the column widths are known before the data
             is, so they render immediately and nothing shifts when the
             rows land. */
          <Table sx={{ width: "100%", tableLayout: "fixed" }}>
            <TableHead><TableRow>
              <TableCell sx={{ ...headCellSx, width: "5rem" }}>Rank</TableCell>
              <TableCell sx={headCellSx}>Grade</TableCell>
              <TableCell sx={headCellSx}>Band (min – max CTC)</TableCell>
              <TableCell sx={{ ...headCellSx, width: "7rem" }} align="right">Actions</TableCell>
            </TableRow></TableHead>
            <TableBody><TableSkeleton columns={4} /></TableBody>
          </Table>
        ) : error ? <EmptyState variant="error" title="Couldn't load grades" onRetry={fetchData} /> : levels.length === 0 ? (
          <EmptyState title="No grades defined" description="Create levels like L1, L2, L3 with their salary bands." />
        ) : (
          <Table sx={{ width: "100%", tableLayout: "fixed" }}>
            <TableHead><TableRow>
              <TableCell sx={{ ...headCellSx, width: "5rem" }}>Rank</TableCell>
              <TableCell sx={headCellSx}>Grade</TableCell>
              <TableCell sx={headCellSx}>Band (min – max CTC)</TableCell>
              <TableCell sx={{ ...headCellSx, width: "7rem" }} align="right">Actions</TableCell>
            </TableRow></TableHead>
            <TableBody>{levels.map((l) => (
              <TableRow key={l._id}>
                <TableCell sx={bodyCellSx}>{l.rank}</TableCell>
                <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{l.name}{l.description ? <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, fontWeight: 500 }}>{l.description}</Box> : null}</TableCell>
                <TableCell align="right" sx={bodyCellSx}>{l.band?.minCTC || l.band?.maxCTC ? `${money(l.band?.minCTC)} – ${money(l.band?.maxCTC)}` : "—"}</TableCell>
                <TableCell sx={bodyCellSx} align="right">
                  <IconButton aria-label="Edit" size="small" onClick={() => openEdit(l)} sx={{ color: tokens.textMuted }}>✎</IconButton>
                  <IconButton aria-label="Delete" size="small" onClick={() => remove(l._id)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        )}
      </MainBox>

      <Dialog open={dialog} onClose={() => setDialog(false)} PaperProps={{ sx: { borderRadius: "12px", width: "min(30rem,92vw)" } }}>
        <Box sx={{ p: "1.5rem" }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb="1.25rem">
            <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>{editing ? "Edit grade" : "New grade"}</Box>
            <IconButton aria-label="Close" size="small" onClick={() => setDialog(false)}><CloseIcon fontSize="small" /></IconButton>
          </Stack>
          <Stack direction="row" spacing={1.25} sx={{ mb: "1rem" }}>
            <Box sx={{ flex: 1 }}><Label>Grade name</Label><TextField fullWidth size="small" value={form.name} placeholder="e.g. L2" onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></Box>
            <Box sx={{ width: "6rem" }}><Label>Rank</Label><TextField fullWidth size="small" type="number" value={form.rank} onChange={(e) => setForm((f) => ({ ...f, rank: e.target.value }))} /></Box>
          </Stack>
          <Stack direction="row" spacing={1.25} sx={{ mb: "1rem" }}>
            <Box sx={{ flex: 1 }}><Label>Band min CTC</Label><TextField fullWidth size="small" type="number" value={form.minCTC} placeholder="0 = none" onChange={(e) => setForm((f) => ({ ...f, minCTC: e.target.value }))} /></Box>
            <Box sx={{ flex: 1 }}><Label>Band max CTC</Label><TextField fullWidth size="small" type="number" value={form.maxCTC} placeholder="0 = none" onChange={(e) => setForm((f) => ({ ...f, maxCTC: e.target.value }))} /></Box>
          </Stack>
          <Label>Description (optional)</Label>
          <TextField fullWidth size="small" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} sx={{ mb: "1.5rem" }} />
          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <BtnSecondary onClick={() => setDialog(false)} sx={{ borderRadius: "8px" }}>Cancel</BtnSecondary>
            <BtnPrimary onClick={submit} disabled={busy} sx={{ borderRadius: "8px" }}>{busy ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Save grade"}</BtnPrimary>
          </Stack>
        </Box>
      </Dialog>
    </Box>
  );
};

export default GradesBands;
